export function parseReadingBpm(d) {
  if (!d || typeof d !== "object") return 0;
  for (const key of ["hr_value", "heart_rate", "hr", "bpm"]) {
    const n = Number(d[key]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function parseGraphTimeToSeconds(time) {
  if (time == null) return NaN;
  if (typeof time === "number" && Number.isFinite(time)) return time;
  const s = String(time).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return NaN;
}

export function buildHrPointsFromInfo(info) {
  if (!info) {
    return { hrPoints: [], durationFromSeries: 0 };
  }

  const readings = info.readings;
  if (Array.isArray(readings) && readings.length > 0) {
    const sorted = [...readings].sort((a, b) => {
      const as = Number(a.recorded_sec) || 0;
      const bs = Number(b.recorded_sec) || 0;
      if (as !== bs) return as - bs;
      const ta = a.recorded_at ? new Date(a.recorded_at).getTime() : 0;
      const tb = b.recorded_at ? new Date(b.recorded_at).getTime() : 0;
      return ta - tb;
    });
    const hrPoints = sorted
      .map((d) => ({ bpm: parseReadingBpm(d) }))
      .filter((d) => d.bpm > 0);
    const t0 = Number(sorted[0]?.recorded_sec);
    const t1 = Number(sorted[sorted.length - 1]?.recorded_sec);
    const hasSec =
      Number.isFinite(t0) && Number.isFinite(t1) && sorted.length > 0;
    let durationSec = hasSec ? Math.max(0, t1 - t0) : 0;
    if (durationSec === 0 && sorted.length >= 2) {
      const w0 = sorted[0]?.recorded_at
        ? new Date(sorted[0].recorded_at).getTime()
        : NaN;
      const w1 = sorted[sorted.length - 1]?.recorded_at
        ? new Date(sorted[sorted.length - 1].recorded_at).getTime()
        : NaN;
      if (Number.isFinite(w0) && Number.isFinite(w1) && w1 >= w0) {
        durationSec = Math.round((w1 - w0) / 1000);
      }
    }
    if (hrPoints.length > 0) {
      return { hrPoints, durationFromSeries: durationSec };
    }
  }

  const graph = info.hrGraph;
  if (Array.isArray(graph) && graph.length > 0) {
    const pts = [];
    for (const d of graph) {
      const t = parseGraphTimeToSeconds(d.time);
      const hr = parseReadingBpm(d);
      if (!Number.isFinite(t) || t < 0 || hr <= 0) continue;
      pts.push({ bpm: hr, t });
    }
    pts.sort((a, b) => a.t - b.t);
    const hrPoints = pts.map((p) => ({ bpm: p.bpm }));
    const durationSec =
      pts.length >= 2 ? Math.max(0, pts[pts.length - 1].t - pts[0].t) : 0;
    return { hrPoints, durationFromSeries: durationSec };
  }

  return { hrPoints: [], durationFromSeries: 0 };
}

export function extractHrStats(info) {
  const maxHr = Number(info?.max_hr ?? info?.maxHR);
  const minHr = Number(info?.min_hr ?? info?.minHR);
  const avgHr = Number(info?.avg_hr ?? info?.averageHR);
  const summaryDurationSec = Number(info?.summary_duration_sec);
  const { hrPoints, durationFromSeries } = buildHrPointsFromInfo(info);
  return {
    maxHr: Number.isFinite(maxHr) && maxHr > 0 ? maxHr : null,
    minHr: Number.isFinite(minHr) && minHr > 0 ? minHr : null,
    avgHr: Number.isFinite(avgHr) && avgHr > 0 ? avgHr : null,
    summaryDurationSec:
      Number.isFinite(summaryDurationSec) && summaryDurationSec > 0
        ? summaryDurationSec
        : durationFromSeries,
    hrPoints,
  };
}
